import React from 'react';
import {
  AutomationActionType,
  AutomationType,
  CandidateBatch
} from 'generated-graphql/types';
import { useDrawer } from 'services/drawer/hooks';
import { useAnalysisAutomationTasks } from 'modules/analyse/hooks/useAnalysisAutomationTasks';
import { Loading } from 'common/ui/Loading';
import { ActionsSettings } from './ActionsSettings';
import { AutomationDrawerContainer } from './AutomationDrawerContainer';
import { AlertCard, BulletList } from 'common/ui';
import { useConfirmationModal } from 'services/modals/hooks';
import { Stack, Typography } from '@mui/material';
import { FormikErrors } from 'formik';
import { AnalysisAutomationItem } from 'modules/analyse/types/AutomationTasks';
import { Button } from 'system/atoms';

const minWidth = '750px';

type Props = {
  candidateBatch: CandidateBatch;
  setCandidateBatch: (candidateBatch: CandidateBatch) => void;
};

const confirmModalMessageElement = (listOfActions: string[]) => (
  <Stack direction="column">
    <Typography>
      The following will be performed, and cannot be undone:
    </Typography>
    <BulletList items={listOfActions} />
  </Stack>
);

const getActionText = (automationActionType: AutomationActionType): string => {
  switch (automationActionType) {
    case AutomationActionType.UpdateAts:
      return 'ATS statuses updated';
    case AutomationActionType.SendCampaign:
      return 'Candidates added to campaign(s)';
    case AutomationActionType.SendJobRecommendationEmail:
      return 'Job recommendations sent';
    case AutomationActionType.SendEmail:
      return 'Rejection messages sent';
    default:
      return '';
  }
};

const filterActions = (automationTasks: AnalysisAutomationItem[]) => {
  const listOfActions: AutomationActionType[] = [];

  return automationTasks
    .filter((task) => {
      if (listOfActions.includes(task.action.type)) {
        return false;
      }
      listOfActions.push(task.action.type);
      return true;
    })
    .map<string>((task) => getActionText(task.action.type));
};

export const AnalysisActionsDrawer: React.FC<Props> = ({
  candidateBatch,
  setCandidateBatch
}) => {
  const { forceCloseDrawer } = useDrawer();
  const { showModal } = useConfirmationModal();

  const { formik, loading, isSubmitting, isSaving, onFormChanged } =
    useAnalysisAutomationTasks({
      candidateBatch,
      setCandidateBatch,
      automationType: AutomationType.Action
    });

  const saveOnlyClicked = async () => {
    await formik.setFieldValue('saveOnly', true);
    await formik.submitForm();
  };

  const saveAndActionClicked = async () => {
    const automationTasks = formik.values.automationTasks;

    const actions = filterActions(automationTasks);

    if (actions.length === 0) {
      await formik.setFieldValue('saveOnly', false);
      await formik.submitForm();
    }

    showModal({
      title: 'Action analysis?',
      message: confirmModalMessageElement(actions),
      confirmationText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: async () => {
        await formik.setFieldValue('saveOnly', false);
        await formik.submitForm();
      }
    });
  };

  if (loading) {
    return (
      <Loading
        containerSx={{
          minWidth
        }}
      />
    );
  }

  return (
    <AutomationDrawerContainer
      title="Actions"
      subtitle="Depending on an applicant's status, automatically update your ATS or send them live campaigns."
      candidateBatch={candidateBatch}
      loading={loading}
      onCancel={forceCloseDrawer}
      additionalComponents={
        <AlertCard type="info" title="Actions cannot be undone">
          Applied to all candidates, including locked ones.
        </AlertCard>
      }
      overrideActionButtons={
        <>
          <Button
            variant="primary"
            onClick={saveAndActionClicked}
            disabled={isSubmitting || isSaving}
            loading={isSubmitting}
          >
            Save and action
          </Button>
          <Button
            variant="secondary"
            onClick={saveOnlyClicked}
            disabled={isSaving || isSubmitting}
            loading={isSaving}
          >
            Save only
          </Button>
        </>
      }
      showLastUpdate
    >
      <ActionsSettings
        atsJobId={candidateBatch.atsJobId}
        candidateBatchId={candidateBatch.id}
        analysisAutomationItems={formik.values.automationTasks}
        onFormChange={async (values) => {
          await formik.setFieldValue('automationTasks', values);
          await formik.validateForm();
          onFormChanged(values);
        }}
        enableReinitialize
        errors={
          formik.errors.automationTasks
            ? (formik.errors
                .automationTasks as FormikErrors<AnalysisAutomationItem>[])
            : undefined
        }
      />
    </AutomationDrawerContainer>
  );
};
